import { useNavigate } from 'react-router-dom';
import { useDoubleBenefit } from '@/hooks/useDoubleBenefit';
import { DOUBLE_BENEFIT_PATH } from '@/constants/proTerms';
import { PRO_GRADIENT } from '@/lib/themePalette';
import { doubleBenefitOn } from '@/lib/rubProvider';
import { STRIPE_PRIMARY } from '@/lib/visitorRegion';

/**
 * Плашка «ДВОЙНАЯ ВЫГОДА» — одна на весь сайт.
 *
 * Состояние человека решает хук, а он читает готовый ответ сервера. Поэтому
 * плашка одинаково честна и в кабинете, и на витрине: если сервер не даст
 * купить за 499, плашки просто не будет.
 *
 * Цвет — токены accent-pro: это фирменный акцент платного. Хардкодить
 * фиолетовый нельзя, на второй сборке сайта (REACT_APP_THEME=violet) тот же
 * токен становится золотом, и плашка едет вместе с темой.
 *
 * place — куда врезана плашка, уходит в метрику: lk_slot / pricing / home /
 * checkout / programs.
 */
/** Зачёркнутая обычная цена («999», «9,99 $») и пробел; нет её — ничего. */
function old(db, cls) {
  if (!db.basePriceLabel) return null;
  return <><s className={cls}>{db.basePriceLabel}</s>{' '}</>;
}

export default function DoubleBenefitBanner({ place, className = '', variant = 'card' }) {
  const navigate = useNavigate();
  const db = useDoubleBenefit();

  if (db.state === 'loading' || db.state === 'none') return null;

  const isGuest = db.state === 'guest';
  // Без акции (Stripe-путь иностранца на ру, Робокасса): кнопка кабинета
  // обещает ДВОЙНУЮ ВЫГОДУ и гостю — её нет совсем. Гостевая плашка витрины
  // об акции молчит («14 дней бесплатно»), она остаётся.
  if (!(doubleBenefitOn() || STRIPE_PRIMARY) && (!isGuest || variant === 'button')) return null;

  const go = () => {
    import('@/lib/analytics')
      .then(({ trackEvent }) => trackEvent('double_benefit_click', { place }))
      .catch(() => {});
    navigate(isGuest ? '/auth/register' : DOUBLE_BENEFIT_PATH);
  };

  const formatDeadline = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    } catch {
      return '';
    }
  };

  // ==================== Кабинет: одна большая кнопка ====================
  // Кнопки внутри кнопки быть не может, поэтому весь блок — единый <button>:
  // куда ни ткни, попадёшь на оплату. Текст белый по фиолетовому, числа
  // живые: N уменьшается каждый день вместе с остатком триала.
  if (variant === 'button') {
    return (
      <button
        type="button"
        onClick={go}
        className={`w-full text-left rounded-xl px-3 py-2.5 text-white transition-opacity hover:opacity-95 ${className}`}
        style={{ background: PRO_GRADIENT, border: 'none' }}
        data-testid={`double-benefit-${place}`}
      >
        <span className="block text-lg font-extrabold tracking-tight leading-none mb-1">
          ДВОЙНАЯ ВЫГОДА
        </span>
        {isGuest ? (
          <span className="block text-xs leading-snug">
            Зарегистрируйся — 14 дней бесплатно + ДВОЙНАЯ ВЫГОДА на ПРО
          </span>
        ) : db.creditLine ? (
          <span className="block text-xs leading-snug" data-testid={`double-benefit-credit-${place}`}>
            {db.creditLine}
          </span>
        ) : db.state === 'trial' ? (
          <>
            <span className="block text-xs leading-snug">
              Оформи ПРО сегодня за {old(db, 'opacity-70')}
              <b>{db.priceLabel}</b> и получи ещё {db.bonusDays}{' '}
              {daysWord(db.bonusDays)} в подарок!
            </span>
            <span className="block text-xs leading-snug mt-0.5">
              Ты получишь {30 + db.bonusDays} {daysWord(30 + db.bonusDays)} ПРО за {db.priceLabel}.
            </span>
          </>
        ) : (
          <span className="block text-xs leading-snug">
            Оформи ПРО{formatDeadline(db.deadlineDate) ? ` до ${formatDeadline(db.deadlineDate)}` : ''} за{' '}
            {old(db, 'opacity-70')}<b>{db.priceLabel}</b> — первый
            месяц со скидкой 50 %!
          </span>
        )}
      </button>
    );
  }

  // ==================== Витрины: плашка с кнопкой ====================
  let body = null;
  let cta = 'Подключить ПРО';

  if (isGuest) {
    // Гостю ни про ПРО, ни про саму акцию не говорим (решение владельца):
    // на его пути рекламы нет, первое предложение ПРО человек видит в
    // кабинете. Остаётся бесплатный шаг — регистрация. Заголовок «ДВОЙНАЯ
    // ВЫГОДА» у гостя тоже не печатается, см. ниже.
    body = <>Зарегистрируйся — 14 дней бесплатно</>;
    cta = 'Зарегистрироваться';
  } else if (db.creditLine) {
    // Покупательнице разовых — её цена с зачётом (backend/first_month_credit.py).
    body = <span data-testid={`double-benefit-credit-${place}`}>{db.creditLine}</span>;
  } else if (db.state === 'trial') {
    const total = 30 + db.bonusDays;
    body = (
      <>
        ПРО {old(db, 'text-slate-400 font-normal')}
        <b>{db.priceLabel}</b> за первый месяц + {db.bonusDays}{' '}
        {daysWord(db.bonusDays)} в подарок — ПРО на {total} дней
      </>
    );
  } else {
    const until = formatDeadline(db.deadlineDate);
    body = (
      <>
        Пробный период закончился. ПРО{' '}
        {old(db, 'text-slate-400 font-normal')}
        <b>{db.priceLabel}</b> за первый месяц{until ? ` — до ${until}` : ''}
      </>
    );
  }

  return (
    <div
      className={`rounded-xl border border-accent-pro-200 bg-accent-pro-50 px-3 py-2.5 ${className}`}
      data-testid={`double-benefit-${place}`}
    >
      {!isGuest && (
        <p className="text-base md:text-lg font-extrabold tracking-tight text-accent-pro-700 leading-none mb-1">
          ДВОЙНАЯ ВЫГОДА
        </p>
      )}
      <p className="text-xs text-slate-700 leading-snug mb-2">{body}</p>
      <button
        type="button"
        onClick={go}
        className="w-full h-9 rounded-lg bg-accent-pro-600 hover:bg-accent-pro-700 text-white text-xs font-bold transition-colors"
        data-testid={`double-benefit-cta-${place}`}
      >
        {cta}
      </button>
    </div>
  );
}

function daysWord(n) {
  const last = n % 10;
  const two = n % 100;
  if (two >= 11 && two <= 14) return 'дней';
  if (last === 1) return 'день';
  if (last >= 2 && last <= 4) return 'дня';
  return 'дней';
}
